import React from 'react'
import PropTypes from 'prop-types'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import './MetricCard.css'

/**
 * MetricCard Component
 *
 * Displays a single metric with value, trend indicator, and optional comparison.
 *
 * @param {Object} props
 * @param {string} props.title - Metric title/label
 * @param {string|number} props.value - Primary value to display
 * @param {string} props.subtitle - Optional subtitle text
 * @param {number} props.change - Percentage change (positive/negative)
 * @param {string} props.changeLabel - Label for change (e.g., "vs last month")
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.variant - Color variant (primary, success, warning, danger, info)
 * @param {string} props.size - Size variant (small, medium, large)
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onClick - Click handler
 */
const MetricCard = ({
	title,
	value,
	subtitle,
	change,
	changeLabel = 'vs last period',
	icon,
	variant = 'primary',
	size = 'medium',
	loading = false,
	onClick,
}) => {
	const getTrendIcon = () => {
		if (change === undefined || change === null) return null
		if (change > 0) return <TrendingUp size={14} />
		if (change < 0) return <TrendingDown size={14} />
		return <Minus size={14} />
	}

	const getTrendClass = () => {
		if (change === undefined || change === null) return ''
		if (change > 0) return 'metric-card__trend--positive'
		if (change < 0) return 'metric-card__trend--negative'
		return 'metric-card__trend--neutral'
	}

	const formatChange = () => {
		if (change === undefined || change === null) return null
		const sign = change > 0 ? '+' : ''
		return `${sign}${change}%`
	}

	const cardClasses = [
		'metric-card',
		`metric-card--${variant}`,
		`metric-card--${size}`,
		onClick ? 'metric-card--clickable' : '',
		loading ? 'metric-card--loading' : '',
	]
		.filter(Boolean)
		.join(' ')

	return (
		<div className={cardClasses} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
			{loading ? (
				<div className="metric-card__skeleton">
					<div className="metric-card__skeleton-title"></div>
					<div className="metric-card__skeleton-value"></div>
					<div className="metric-card__skeleton-subtitle"></div>
				</div>
			) : (
				<>
					<div className="metric-card__header">
						{icon && <div className="metric-card__icon">{icon}</div>}
						<span className="metric-card__title">{title}</span>
					</div>

					<div className="metric-card__body">
						<div className="metric-card__value">{value}</div>

						{subtitle && <div className="metric-card__subtitle">{subtitle}</div>}
					</div>

					{change !== undefined && change !== null && (
						<div className={`metric-card__trend ${getTrendClass()}`}>
							{getTrendIcon()}
							<span className="metric-card__trend-value">{formatChange()}</span>
							<span className="metric-card__trend-label">{changeLabel}</span>
						</div>
					)}
				</>
			)}
		</div>
	)
}

MetricCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	subtitle: PropTypes.string,
	change: PropTypes.number,
	changeLabel: PropTypes.string,
	icon: PropTypes.node,
	variant: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
	size: PropTypes.oneOf(['small', 'medium', 'large']),
	loading: PropTypes.bool,
	onClick: PropTypes.func,
}

export default MetricCard
